import React, { useState } from "react";
import { X, Minus, Plus, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { useTrolley } from "../../context/TrolleyContext";

export const ProductModal = ({ product, onClose }) => {
  const { add } = useTrolley();
  const available = (product.variants || []).filter((v) => v.available);
  const [variantId, setVariantId] = useState(available[0]?.variant_id || "");
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const variant = (product.variants || []).find((v) => v.variant_id === variantId);

  const doAdd = () => {
    if (!variant) return;
    add(product, variant, qty);
    toast.success(`${product.title} hopped into your trolley!`, { icon: <Flower2 className="w-4 h-4 text-magenta" /> });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="supply-product-modal">
      <div className="absolute inset-0 bg-plum-deep/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-cream border-[3px] border-plum-deep rounded-[2rem] w-full max-w-3xl max-h-[88vh] overflow-y-auto animate-pop-in">
        <button onClick={onClose} data-testid="supply-modal-close" aria-label="Close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-plum-deep text-cream flex items-center justify-center hover:bg-magenta transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="grid md:grid-cols-2 gap-0">
          <div className="p-5">
            <div className="aspect-square rounded-[1.5rem] overflow-hidden bg-blush border-2 border-plum-deep/15">
              {product.images?.[img] && <img src={product.images[img]} alt={product.title} className="w-full h-full object-cover" />}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {product.images.slice(0, 6).map((u, i) => (
                  <button key={u} onClick={() => setImg(i)}
                    className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-colors ${i === img ? "border-magenta" : "border-plum-deep/15"}`}>
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-5 md:pr-8 md:py-7">
            <p className="text-[10px] uppercase tracking-[0.24em] text-plum/70 font-bold">{product.category} · {product.supplier}</p>
            <h2 className="font-serif-display uppercase text-plum-deep text-2xl mt-1 leading-tight">{product.title}</h2>
            <p className="font-bold text-plum-deep text-xl mt-2" data-testid="supply-modal-price">${(variant?.price ?? product.price_from)?.toFixed(2)} NZD</p>
            {product.description && (
              <p className="text-sm text-plum-deep/75 mt-3 whitespace-pre-line max-h-40 overflow-y-auto">{product.description}</p>
            )}
            {(product.variants || []).length > 1 && (
              <label className="block mt-4">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">PICK YOUR OPTION</span>
                <select value={variantId} onChange={(e) => setVariantId(e.target.value)} data-testid="supply-variant-select"
                  className="w-full rounded-full px-4 py-3 bg-warmwhite border-2 border-plum-deep/20 text-sm focus:outline-none focus:border-magenta">
                  {product.variants.map((v) => (
                    <option key={v.variant_id} value={v.variant_id} disabled={!v.available}>
                      {v.title}{!v.available ? " — SOLD OUT" : ""} · ${v.price.toFixed(2)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="flex items-center gap-4 mt-5">
              <div className="flex items-center gap-1 bg-warmwhite border-2 border-plum-deep/20 rounded-full px-1.5 py-1">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="supply-qty-minus" className="w-8 h-8 rounded-full hover:bg-blush flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-bold" data-testid="supply-qty">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(99, q + 1))} data-testid="supply-qty-plus" className="w-8 h-8 rounded-full hover:bg-blush flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={doAdd} disabled={!variant} data-testid="supply-modal-add"
                className="flex-1 rounded-full bg-magenta text-cream py-3.5 font-serif-display uppercase text-sm tracking-[0.16em] font-bold hover:bg-plum transition-all active:scale-95 disabled:opacity-40">
                ADD TO TROLLEY
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
